import {
  checkUserRole,
  getItem,
  hasRoles,
  isManager,
  isSeller,
  someTrue,
} from "../../lib/utility";

/**
 * Route access rules mirrored from Sidebar.jsx LinkItem conditions.
 * Longest-prefix match is used so deep links inherit the parent entry.
 *
 * Optional fields (same AND semantics as LinkItem):
 * - roles: roleCondition string
 * - navOption: single key or array of keys (all required)
 * - someNavOptions: comma-separated keys (any truthy via someTrue)
 * - requireNotTenant: customValidation !checkUserRole(user, "tenant")
 * - requireDlvSmsc / requireNoDlvSmsc: localStorage shouldShowDlvSmsc
 */

const ALWAYS_ALLOWED = [
  "/dashboard",
  "/profile",
  "/downloads",
  "/developer/sdk",
  "/api-runner",
  "/update-password",
  "/wallet",
  "/user/abac",
  "/user/multi-access",
];

/** Paths available to seller/manager sidebar tree (plus always-allowed). */
const SELLER_MANAGER_RULES = [
  { path: "/reports/batch-summary" },
  { path: "/reports/submission" },
  { path: "/reports/delivery-advance" },
  { path: "/reports/coverage" },
  { path: "/reports/performance-user" },
  { path: "/sales-list", roles: "manager" },
  { path: "/reports/user-status" },
  { path: "/reports/transaction-history" },
  { path: "/view/schedule" },
  { path: "/view/bulk" },
];

/**
 * Staff (non-seller/manager) sidebar paths.
 * Parent navOption/roles are baked into children so matching a leaf is enough.
 */
const STAFF_RULES = [
  // Messages
  { path: "/message", navOption: "sms" },

  // Utilities
  { path: "/utilities/edit/bulk", navOption: "utility" },
  { path: "/utilities/edit/schedule", navOption: "utility" },
  { path: "/utilities/two-way", navOption: ["utility", "twoWay"] },
  { path: "/utilities/two-way-report", navOption: ["utility", "twoWay"] },
  { path: "/utilities/converter", navOption: "utility" },
  { path: "/utilities/subscription", navOption: "utility" },
  {
    path: "/utilities/pcaps",
    navOption: "utility",
    roles: "superadmin,system",
  },

  // Reports (parent navOption: report)
  { path: "/reports/campaign/aborted", navOption: "report" },
  { path: "/reports/campaign/campaign", navOption: "report" },
  { path: "/reports/campaign/schedule", navOption: "report" },
  { path: "/reports/summary/delivery", navOption: "report" },
  { path: "/reports/summary/batch", navOption: "report" },
  { path: "/reports/detailed/advanced", navOption: "report" },
  { path: "/reports/detailed/content", navOption: "report" },
  {
    path: "/reports/detailed/smsc",
    navOption: "report",
    roles: "admin,tenant",
    requireDlvSmsc: true,
  },
  { path: "/reports/latency", navOption: "report" },
  { path: "/reports/consumption", navOption: "report" },
  { path: "/reports/lookup", navOption: "report" },
  {
    path: "/reports/aggregated",
    navOption: "report",
    roles: "superadmin,admin,tenant,system",
  },
  {
    path: "/reports/profit",
    navOption: "report",
    roles: "superadmin,admin,tenant,system",
  },
  {
    path: "/reports/performance/user",
    navOption: "report",
    roles: "superadmin,system",
  },
  {
    path: "/reports/performance/route",
    navOption: "report",
    roles: "superadmin,system",
  },
  {
    path: "/reports/blocked",
    navOption: "report",
    roles: "superadmin,system",
  },
  {
    path: "/reports/tps",
    navOption: "report",
    roles: "admin,tenant,system,superadmin",
  },
  {
    path: "/reports/vendor-summary",
    navOption: "report",
    roles: "admin,tenant,system,superadmin",
  },
  {
    path: "/reports/edr",
    navOption: "report",
    roles: "admin,tenant,system,superadmin",
  },
  {
    path: "/reports/coverage-report",
    navOption: "report",
    roles: "superadmin,system,tenant",
  },
  // Admin/tenant coverage only when delivery SMSC menu is hidden
  {
    path: "/reports/coverage-report",
    navOption: "report",
    roles: "admin,tenant",
    requireNoDlvSmsc: true,
  },

  // Address book / templates
  { path: "/address-book", navOption: "addBook" },
  { path: "/template", navOption: "template" },

  // Approvals — admin/tenant need web-access flags
  {
    path: "/approvals/bulk",
    roles: "admin,tenant",
    someNavOptions: "bulk_act,bulk_mgmt",
  },
  {
    path: "/approvals/sender",
    roles: "admin,tenant",
    someNavOptions: "sender_act,sender_mgmt",
  },
  // Approvals — superadmin/system (no nav flags)
  { path: "/approvals/bulk", roles: "superadmin,system" },
  { path: "/approvals/sender", roles: "superadmin,system" },

  // User
  { path: "/user", navOption: "user", roles: "admin,tenant" },
  { path: "/user", navOption: "user", roles: "superadmin,system" },
  // Users sidebar link points to /user/list — admin/tenant must match here too
  // (longest-prefix wins, so /user rules above do not apply to /user/list).
  { path: "/user/list", navOption: "user", roles: "admin,tenant" },
  { path: "/user/list", navOption: "user", roles: "superadmin,system" },
  { path: "/user/limit", navOption: "user", roles: "superadmin,system" },

  // Error codes
  { path: "/user/error-code" },
  { path: "/smsc/error-code", roles: "superadmin,system,tenant" },

  // Routing
  {
    path: "/routing/add",
    navOption: "routing",
    roles: "tenant,superadmin,system",
  },
  {
    path: "/routing/edit",
    navOption: "routing",
    roles: "superadmin,system,admin,tenant",
  },
  {
    path: "/routing/copy",
    navOption: "routing",
    roles: "superadmin,system,admin,tenant",
  },
  {
    path: "/routing/sender",
    navOption: "routing",
    roles: "superadmin,system",
  },

  // Company / vendor
  { path: "/company", roles: "superadmin,system,admin,tenant" },
  { path: "/vendor", roles: "superadmin,system,tenant" },

  // Billing (routed feature, no sidebar entry — keep role-gated access)
  { path: "/billing", roles: "admin,tenant,superadmin,system" },

  // SMSC
  { path: "/smsc/list", roles: "superadmin,system,tenant" },
  { path: "/smsc/flag", roles: "superadmin,system,tenant" },
  { path: "/smsc/status", roles: "superadmin,system,tenant" },
  { path: "/smsc/http", roles: "superadmin,system" },
  {
    path: "/smsc/alert",
    roles: "superadmin,system,tenant",
    requireNotTenant: true,
  },
  {
    path: "/smsc/limits",
    roles: "superadmin,system,tenant",
    requireNotTenant: true,
  },
  { path: "/smsc/looping", roles: "superadmin,system,tenant" },
  { path: "/smsc/hlr", roles: "superadmin,system" },
  { path: "/smsc/hlr-flag", roles: "superadmin,system" },
  { path: "/smsc/hlr-status", roles: "superadmin,system" },
  { path: "/smsc/blocking", roles: "superadmin,system,tenant" },
  { path: "/smsc/schedule", roles: "superadmin,system,tenant" },
  { path: "/smsc/special-settings", roles: "superadmin,system,tenant" },
  { path: "/smsc/group-data", roles: "superadmin,system,tenant" },

  // Firewall / DLT / sales / network
  {
    path: "/firewall",
    navOption: "bsfm",
    roles: "admin,tenant,system,superadmin",
  },
  { path: "/dlt", roles: "superadmin,system" },
  {
    path: "/sales",
    navOption: "sales",
    roles: "admin,tenant,superadmin,system",
  },
  {
    path: "/network",
    navOption: "sales",
    roles: "superadmin,system",
  },
];

const normalizePath = (pathname = "") => {
  if (!pathname) return "/";
  const trimmed = pathname.split("?")[0].split("#")[0];
  if (trimmed.length > 1 && trimmed.endsWith("/")) {
    return trimmed.replace(/\/+$/, "") || "/";
  }
  return trimmed || "/";
};

const getShouldShowDlvSmsc = () => {
  const stored = getItem("shouldShowDlvSmsc", true);
  return stored ?? false;
};

const matchesNavOptions = (rule, navOptions) => {
  if (!rule.navOption) return true;
  const keys = Array.isArray(rule.navOption)
    ? rule.navOption
    : [rule.navOption];
  return keys.every((key) => !!navOptions?.[key]);
};

const evaluateRule = (rule, user, navOptions) => {
  if (rule.roles !== undefined && !hasRoles(rule.roles, user)) {
    return false;
  }
  if (!matchesNavOptions(rule, navOptions)) {
    return false;
  }
  if (
    rule.someNavOptions !== undefined &&
    !someTrue(navOptions, rule.someNavOptions)
  ) {
    return false;
  }
  if (rule.requireNotTenant && checkUserRole(user, "tenant")) {
    return false;
  }
  if (rule.requireDlvSmsc && !getShouldShowDlvSmsc()) {
    return false;
  }
  if (rule.requireNoDlvSmsc && getShouldShowDlvSmsc()) {
    return false;
  }
  return true;
};

/**
 * Find all rules whose path is a prefix of pathname.
 * Only the longest matching path length is kept so a denied child
 * (e.g. /user/limit) does not fall back to a looser parent (/user).
 */
const findMatchingRules = (rules, pathname) => {
  const path = normalizePath(pathname);
  const matches = rules.filter((rule) => {
    const rulePath = normalizePath(rule.path);
    return path === rulePath || path.startsWith(`${rulePath}/`);
  });
  if (!matches.length) return [];

  const maxLen = Math.max(...matches.map((rule) => normalizePath(rule.path).length));
  return matches.filter((rule) => normalizePath(rule.path).length === maxLen);
};

/**
 * Whether the authenticated user may open pathname, matching sidebar ACL.
 */
export const canAccessSidebarPath = (pathname, user, navOptions = {}) => {
  const path = normalizePath(pathname);

  if (ALWAYS_ALLOWED.some((allowed) => path === allowed || path.startsWith(`${allowed}/`))) {
    return true;
  }

  if (isSeller(user) || isManager(user)) {
    const matches = findMatchingRules(SELLER_MANAGER_RULES, path);
    if (!matches.length) return false;
    // Any matching rule that passes is enough (OR across alternate rules)
    return matches.some((rule) => evaluateRule(rule, user, navOptions));
  }

  const matches = findMatchingRules(STAFF_RULES, path);
  if (!matches.length) return false;
  return matches.some((rule) => evaluateRule(rule, user, navOptions));
};

export const sidebarAccessConfig = {
  ALWAYS_ALLOWED,
  SELLER_MANAGER_RULES,
  STAFF_RULES,
  normalizePath,
};
